// client/src/pages/secure/AgentListPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';
import { getCurrentUser, canManageStaff } from '../../hooks/useCurrentUser';
import AgentInvitesPanel from '../../components/secure/AgentInvitesPanel';

// --- Add Agent Modal Component ---
const AddAgentModal = ({ isOpen, onClose, onAgentAdded }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        licenseNumber: '',
        license_type: 'unknown',
        license_expiration_date: '',
        email: '',
        phone: '',
        status: 'Active',
        hire_date: '',
        termination_date: '',
    });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    useEffect(() => {
        if (isOpen) {
            // Reset form when modal opens
            setFormData({
                firstName: '', lastName: '', licenseNumber: '', license_type: 'unknown',
                license_expiration_date: '', email: '', phone: '', status: 'Active',
                hire_date: '', termination_date: '',
            });
            setError('');
        }
    }, [isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);
        try {
            const newAgent = await apiClient.post('/api/agents', formData);
            onAgentAdded(newAgent);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create agent.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Add New Agent</h2>
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" required className="p-2 border rounded" />
                        <input name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" required className="p-2 border rounded" />
                        <input name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} placeholder="License Number" required className="p-2 border rounded" />
                        <select name="license_type" value={formData.license_type} onChange={handleChange} className="p-2 border rounded">
                            <option value="unknown">Licence type not recorded</option>
                            <option value="broker">Broker</option>
                            <option value="managing_broker">Managing Broker</option>
                            <option value="residential_leasing_agent">Residential Leasing Agent</option>
                        </select>
                        <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Email Address" className="p-2 border rounded" />
                        <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone Number" className="p-2 border rounded" />
                        <div>
                            <label htmlFor="add_hire_date" className="block text-xs font-medium text-slate-600">Sponsorship start</label>
                            <input id="add_hire_date" name="hire_date" type="date" value={formData.hire_date} onChange={handleChange} className="p-2 border rounded w-full" />
                        </div>
                        <div>
                            <label htmlFor="add_license_expiration_date" className="block text-xs font-medium text-slate-600">Licence expires</label>
                            <input id="add_license_expiration_date" name="license_expiration_date" type="date" value={formData.license_expiration_date} onChange={handleChange} className="p-2 border rounded w-full" />
                        </div>
                        <select name="status" value={formData.status} onChange={handleChange} className="p-2 border rounded">
                            <option value="Active">Active</option>
                            <option value="Pending">Pending</option>
                            <option value="Inactive">Inactive</option>
                            <option value="Terminated">Terminated</option>
                        </select>
                        {formData.status === 'Terminated' && (
                            <div>
                                <label htmlFor="add_termination_date" className="block text-xs font-medium text-slate-600">Termination date</label>
                                <input id="add_termination_date" name="termination_date" type="date" value={formData.termination_date} onChange={handleChange} className="p-2 border rounded w-full" />
                            </div>
                        )}
                    </div>
                    <p className="text-xs text-slate-500 mt-3">
                        Illinois licences look like 475.123456. Where the number is not known yet, a value
                        beginning UNVERIFIED- records the gap rather than inventing a number.
                    </p>
                    {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
                    <div className="flex justify-end gap-4 mt-6">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Saving...' : 'Save Agent'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- Agent List Page Component ---
const AgentListPage = () => {
    const [agents, setAgents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const apiClient = useApiClient();

    useEffect(() => {
        apiClient.get('/api/agents')
            .then(data => setAgents(data))
            .catch(err => setError(err.message || 'Failed to fetch agents.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    const handleAgentAdded = (newAgent) => {
        setAgents(prevAgents => [newAgent, ...prevAgents]);
    };

    return (
        <>
            <AddAgentModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                onAgentAdded={handleAgentAdded}
            />
            <div className="bg-white p-8 rounded-lg shadow">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-slate-800">Agent Management</h1>
                    {canManageStaff(getCurrentUser()) && (
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                        >
                            Add New Agent
                        </button>
                    )}
                </div>

                {/* Invite to the app: the managing broker only, like every other way of making a login. */}
                {getCurrentUser()?.isManagingBroker && <div className="mb-6"><AgentInvitesPanel compact /></div>}

                {isLoading && <p>Loading agents...</p>}
                {error && <p className="text-red-500">{error}</p>}
                {!isLoading && !error && (
                    <DataList rows={agents} rowKey="agent_id" csvName="agents" defaultSort={{ key: 'name', dir: 'asc' }}
                        filters={[{ key: 'status', label: 'Status' }]}
                        columns={[
                            { key: 'name', label: 'Name', render: (a) => <Link to={`/dashboard/agents/${a.agent_id}`} className="text-indigo-700 font-medium">{a.name}</Link> },
                            { key: 'license_number', label: 'License #' },
                            { key: 'license_expiration_date', label: 'Expires', type: 'date' },
                            { key: 'email', label: 'Email' },
                            { key: 'status', label: 'Status' },
                            { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (a) => <Link to={`/dashboard/agents/${a.agent_id}`} className="text-indigo-600 hover:text-indigo-900">Manage</Link> },
                        ]} />
                )}
            </div>
        </>
    );
};

export default AgentListPage;